import { useSeo } from "../lib/seo";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-serif text-2xl lg:text-3xl font-medium text-[#0F2A2C] tracking-tight">{title}</h2>
    <div className="mt-3 space-y-3 text-[#4A5568] leading-relaxed">{children}</div>
  </section>
);

export default function Terms() {
  useSeo({
    title: "Service Terms & Conditions | Valora Support Services",
    description: "Service terms and conditions for Valora Support Services — covering NDIS supports, bookings, payments, cancellations and participant rights.",
  });

  return (
    <div data-testid="terms-page" className="bg-white">
      <section className="bg-gradient-to-b from-[#E6FFFA] via-[#F8FBFA] to-white">
        <div className="container-x py-16 lg:py-20">
          <p className="eyebrow">Legal</p>
          <h1 className="mt-3 font-serif text-4xl lg:text-5xl font-medium text-[#0F2A2C] tracking-tight">Service Terms &amp; Conditions</h1>
          <p className="mt-4 max-w-2xl text-[#4A5568]">Last updated: February 2026.</p>
        </div>
      </section>

      <article className="container-x py-14 max-w-3xl">
        <Section title="1. Introduction">
          <p>Welcome to Valora Support Services. We provide NDIS supports including Supported Independent Living (SIL), in-home support, community access, capacity building and support coordination. By engaging our services you agree to the following terms and conditions.</p>
        </Section>

        <Section title="2. Definitions">
          <p>"We", "us", "our" refers to Valora Support Services. "You", "your" refers to the participant or the authorised representative of the NDIS participant. "Services" refers to the NDIS supports and related services we provide.</p>
        </Section>

        <Section title="3. Bookings and quotations">
          <p>All bookings and service commencements must be arranged in advance and are subject to availability. A quotation or service agreement will be provided prior to services starting, which you must accept to confirm the engagement. Payment will be invoiced in line with the agreed billing schedule.</p>
        </Section>

        <Section title="4. Activity allowance">
          <p>Where applicable to your supports (for example, community access or short-stay holidays), we may offer a daily activity allowance set at our discretion to support the best participant experience. This allowance is provided to encourage participation in various activities but is not refundable if not used in the allocated way.</p>
        </Section>

        <Section title="5. Cancellations and refunds">
          <p>Cancellations must be made at least 48 hours prior to the scheduled service. Cancellations made within 48 hours of a scheduled service may be subject to a cancellation fee in line with the current NDIS Pricing Arrangements (typically up to the full agreed cost where short-notice rules apply).</p>
        </Section>

        <Section title="6. Payment">
          <p>Payment for our services is due within 10 days of receipt of the invoice. We accept payment via NDIS funding under the relevant NDIS line items, including through plan managers, the NDIA, or self-managed participants.</p>
        </Section>

        <Section title="7. Rights and responsibilities">
          <p>You are responsible for any damage caused to property during service delivery, excluding normal wear and tear. You agree to use our services in a manner consistent with all applicable laws and regulations and in accordance with these terms and conditions.</p>
        </Section>

        <Section title="8. NDIS rights">
          <p>As an NDIS participant, you have the right to negotiate the terms of our service agreement, including service delivery, price and cancellation policy. We aim to make our service agreements clear and understandable, in line with NDIS guidelines.</p>
        </Section>

        <Section title="9. Limitation of liability">
          <p>To the extent permitted by law, we disclaim all liability for any loss or damage, including any consequential loss or indirect damage, loss of data or profit, or damage to health, whether based in contract, tort (including negligence) or otherwise, suffered in connection with the use of our services. Nothing in these terms limits any rights you have under the Australian Consumer Law or other laws that cannot lawfully be excluded.</p>
        </Section>

        <Section title="10. Privacy and confidentiality">
          <p>We collect and use your personal information in accordance with our <a href="/privacy" className="text-[#0B6E70] font-medium underline">Privacy Policy</a>. We will not share your personal information with anyone without your consent, except where required by law. By using our services, you agree to such collection and use.</p>
        </Section>

        <Section title="11. Dispute resolution">
          <p>Any disputes arising out of or relating to these terms and conditions or our services will be resolved in accordance with the laws of Queensland and the Commonwealth of Australia.</p>
        </Section>

        <Section title="12. Changes to terms and conditions">
          <p>We reserve the right to modify these terms and conditions at any time. We will notify you of any changes by posting the new terms and conditions on our website. Your continued use of our services after any such changes constitutes your acceptance of the new terms and conditions.</p>
        </Section>

        <Section title="13. Contact us">
          <p>
            Valora Support Services<br />
            13 Appleberry Avenue, Coomera QLD 4209<br />
            Email: <a href="mailto:info@valorasupportservices.com" className="text-[#0B6E70] font-medium underline">info@valorasupportservices.com</a><br />
            Phone: <a href="tel:0426635885" className="text-[#0B6E70] font-medium underline">0426 635 885</a>
          </p>
          <p>See also our <a href="/privacy" className="text-[#0B6E70] font-medium underline">Privacy Policy</a>.</p>
        </Section>
      </article>
    </div>
  );
}
